import { isValidTarget } from './tools.js';
import type { AddressEntry } from './resources.js';

/**
 * Hands-on access to a resource.
 *
 * Monitoring answers "is it up". The next thing an engineer does is open a
 * session on the thing — an SSH shell, the web UI, telnet on legacy gear,
 * Winbox on a MikroTik. The platform does not tunnel those sessions (HTTP is
 * the exception, because the web proxy already exists): it works out where the
 * door is, checks whether it is open, and hands over a link the operator's own
 * client can take.
 *
 * Ports come from the resource config when set, and from the service default
 * otherwise. A port of `0` switches a service off, which is the escape hatch
 * for devices where that door is deliberately bricked up.
 *
 * Note this is device access, not authorisation — who may *use* these links is
 * `access.ts`'s business.
 */

export const ACCESS_SERVICES = ['ssh', 'telnet', 'http', 'https', 'winbox', 'ami', 'sip'] as const;
export type AccessService = (typeof ACCESS_SERVICES)[number];

export interface AccessServiceSpec {
  label: string;
  defaultPort: number;
  /** Config key that overrides the port for this service. */
  portKey: string;
  scheme: string;
  /** The URI carries `user@` when the resource has a username configured. */
  usesUsername: boolean;
  /**
   * Whether a desktop handler for the scheme is a safe assumption.
   *
   * `ssh://`, `telnet://` and the web schemes are registered by ordinary
   * desktops. Winbox is not — RouterOS ships no handler, so `winbox://` only
   * works where someone registered it by hand. The UI shows the address for
   * copying rather than pretending the link always fires.
   */
  standardScheme: boolean;
  help: string;
}

export const ACCESS_SERVICE_SPECS: Record<AccessService, AccessServiceSpec> = {
  ssh: {
    label: 'SSH',
    defaultPort: 22,
    portKey: 'sshPort',
    scheme: 'ssh',
    usesUsername: true,
    standardScheme: true,
    help: 'Command line over SSH.',
  },
  telnet: {
    label: 'Telnet',
    defaultPort: 23,
    portKey: 'telnetPort',
    scheme: 'telnet',
    usesUsername: false,
    standardScheme: true,
    help: 'Legacy plaintext console. Closed is the healthy answer here.',
  },
  http: {
    label: 'HTTP',
    defaultPort: 80,
    portKey: 'httpPort',
    scheme: 'http',
    usesUsername: false,
    standardScheme: true,
    help: "The device's web interface, unencrypted.",
  },
  https: {
    label: 'HTTPS',
    defaultPort: 443,
    portKey: 'httpsPort',
    scheme: 'https',
    usesUsername: false,
    standardScheme: true,
    help: "The device's web interface over TLS.",
  },
  winbox: {
    label: 'Winbox',
    defaultPort: 8291,
    portKey: 'winboxPort',
    scheme: 'winbox',
    usesUsername: true,
    standardScheme: false,
    help: 'MikroTik Winbox. Copy the address into Winbox, or register a winbox:// handler on your workstation.',
  },
  ami: {
    label: 'AMI',
    defaultPort: 5038,
    portKey: 'amiPort',
    scheme: 'ami',
    usesUsername: false,
    // No desktop registers an `ami://` handler — this is the Asterisk Manager
    // Interface, a private protocol the platform itself speaks (see the voip
    // plugin's `collectServer`), not something an operator opens by hand. The
    // endpoint exists purely so reachability shows up here, same treatment as
    // Winbox for a scheme with no client to launch.
    standardScheme: false,
    help: 'Asterisk Manager Interface — used by this platform for live status, not meant to be opened directly.',
  },
  sip: {
    label: 'SIP',
    defaultPort: 5060,
    portKey: 'sipPort',
    scheme: 'sip',
    usesUsername: false,
    // Nothing to launch — a softphone registers to this, it doesn't open it.
    // Listed so "is the SIP stack answering" sits next to the other doors.
    standardScheme: false,
    help: 'SIP signalling — checked with a SIP OPTIONS over UDP and a TCP connect; either answering counts as open.',
  },
};

/** Anything with enough shape to work out how to reach it. */
export interface AccessResourceLike {
  kind: string;
  pluginId?: string;
  name?: string;
  address?: string | null;
  secondaryAddresses?: AddressEntry[];
  tags?: string[];
  attributes?: Record<string, unknown>;
  config?: Record<string, unknown>;
}

export interface AccessEndpoint {
  service: AccessService;
  label: string;
  host: string;
  port: number;
  /** URI to hand to the OS. Always present; see `standardScheme` for the caveat. */
  uri: string;
  standardScheme: boolean;
  help: string;
  /** Reachable through the built-in web proxy as well as directly. */
  proxyable: boolean;
  /** The port came from the resource config rather than the service default. */
  custom: boolean;
  /** Filled in by the server's reachability probe; null means "not checked". */
  open: boolean | null;
  /** SIP only: which transport answered the probe. */
  transport?: 'udp' | 'tcp' | null;
  connectMs: number | null;
  /** null for the primary address; the secondary address's own label otherwise. */
  addressLabel: string | null;
}

/* ------------------------------------------------------------------ */
/* Device classification                                               */
/* ------------------------------------------------------------------ */

/** Kinds that live on a rack rather than in a hypervisor — telnet candidates. */
const NETWORK_DEVICE_KINDS = new Set([
  'router',
  'switch',
  'firewall',
  'access-point',
  'voip-gateway',
  'vpn-server',
  'internet-line',
]);

const NETWORK_PLUGIN_IDS = new Set(['routers', 'firewalls', 'networks']);

export function isNetworkDevice(resource: AccessResourceLike): boolean {
  return NETWORK_DEVICE_KINDS.has(resource.kind) || NETWORK_PLUGIN_IDS.has(resource.pluginId ?? '');
}

/**
 * Is this a MikroTik?
 *
 * Checked from several angles because the answer arrives from several places:
 * the operator picks a vendor when adding the device, SNMP reports RouterOS in
 * `sysDescr` on the first poll, and discovery tags devices by MAC OUI. Any one
 * of them is enough — the cost of a false positive is an extra Winbox row.
 */
export function isMikrotik(resource: AccessResourceLike): boolean {
  const vendor = String(resource.config?.vendor ?? resource.attributes?.vendor ?? '').toLowerCase();
  if (vendor === 'mikrotik' || vendor === 'routeros') return true;

  if (/mikrotik|routeros/i.test(resource.kind)) return true;
  if (resource.tags?.some((tag) => /^(mikrotik|routeros)$/i.test(tag))) return true;

  // SNMP fills sysDescr in with "RouterOS CRS326-…" on the first poll, and an
  // operator who named the device "Branch MikroTik" has told us the same thing
  // by hand. Getting this wrong costs one extra row reading "closed", so the
  // net is cast wide on purpose.
  for (const value of [resource.attributes?.sysDescr, resource.attributes?.model, resource.attributes?.osVersion, resource.name]) {
    if (typeof value === 'string' && /mikrotik|routeros/i.test(value)) return true;
  }
  return false;
}

/**
 * Can PCC load balancing be installed on (and read from) this resource? A
 * load balancer, or a MikroTik filed as a router or firewall — a site's edge
 * MikroTik gets added as either.
 */
export function canHostLoadBalancer(resource: AccessResourceLike): boolean {
  if (resource.kind === 'load-balancer') return true;
  return (resource.kind === 'router' || resource.kind === 'firewall') && isMikrotik(resource);
}

/**
 * Is this a Yeastar PBX?
 *
 * Yeastar (S-Series/P-Series/MyPBX) is a Linux-based appliance managed
 * through its web UI/HTTP API — it has no SSH CLI to run a "show config"
 * style command against, unlike a router. Backup logic needs to know this
 * so it doesn't try to SSH into the thing.
 */
export function isYeastar(resource: AccessResourceLike): boolean {
  const vendor = String(resource.config?.vendor ?? resource.attributes?.vendor ?? '').toLowerCase();
  if (vendor === 'yeastar') return true;
  if (/yeastar|mypbx/i.test(resource.kind)) return true;
  if (resource.tags?.some((tag) => /^yeastar$/i.test(tag))) return true;
  for (const value of [resource.attributes?.sysDescr, resource.attributes?.model, resource.name]) {
    if (typeof value === 'string' && /yeastar|mypbx/i.test(value)) return true;
  }
  return false;
}

/**
 * Is this a Dinstar VoIP/GSM gateway?
 *
 * Same story as Yeastar: an HTTP-managed embedded appliance, not an SSH-CLI
 * device.
 */
export function isDinstar(resource: AccessResourceLike): boolean {
  const vendor = String(resource.config?.vendor ?? resource.attributes?.vendor ?? '').toLowerCase();
  if (vendor === 'dinstar') return true;
  if (/dinstar/i.test(resource.kind)) return true;
  if (resource.tags?.some((tag) => /^dinstar$/i.test(tag))) return true;
  for (const value of [resource.attributes?.sysDescr, resource.attributes?.model, resource.name]) {
    if (typeof value === 'string' && /dinstar/i.test(value)) return true;
  }
  return false;
}

/**
 * Is this an Asterisk-based PBX — plain Asterisk, FreePBX, Issabel, ViciDial?
 *
 * Same wide net as `isMikrotik`, and for the same reason: the answer arrives
 * from whichever direction found the box first — the kind an operator picked,
 * the vendor field, a tag, or the hostname somebody typed. What it gates is
 * offering the AMI setup action, so a false positive costs one button that
 * then reports "this did not look like an Asterisk node", while a false
 * negative hides the feature on exactly the PBX that needs it.
 */
export function isAsterisk(resource: AccessResourceLike): boolean {
  const vendor = String(resource.config?.vendor ?? resource.attributes?.vendor ?? '').toLowerCase();
  if (['asterisk', 'freepbx', 'issabel', 'elastix', 'vicidial', 'sangoma'].includes(vendor)) return true;

  // A Yeastar or Dinstar runs Asterisk inside, but as a sealed appliance with
  // no shell to write manager.conf from — excluded deliberately, or this would
  // offer an SSH action against a box that has no SSH.
  if (isYeastar(resource) || isDinstar(resource)) return false;

  if (resource.kind === 'voip-server' || resource.kind === 'vicidial-dialer') return true;
  if (resource.tags?.some((tag) => /^(asterisk|freepbx|pbx|issabel|elastix|vicidial)$/i.test(tag))) return true;

  for (const value of [resource.attributes?.sysDescr, resource.attributes?.model, resource.name]) {
    if (typeof value === 'string' && /asterisk|freepbx|issabel|elastix|vicidial/i.test(value)) return true;
  }
  return false;
}

/**
 * Is this a MagnusBilling softswitch?
 *
 * Asterisk underneath, but its state lives in the `mbilling` database the
 * billing UI manages — so its backup is that database plus config, not a
 * FreePBX dump, and it must be told apart before `isAsterisk` claims it.
 */
export function isMagnusBilling(resource: AccessResourceLike): boolean {
  if (resource.kind === 'magnusbilling') return true;
  const vendor = String(resource.config?.vendor ?? resource.attributes?.vendor ?? '').toLowerCase();
  if (vendor === 'magnusbilling') return true;
  return resource.tags?.some((tag) => /^magnus(billing)?$/i.test(tag)) ?? false;
}

/** Kinds that speak SIP themselves, so a SIP port check means something. */
const SIP_DEVICE_KINDS = new Set(['voip-server', 'voip-gateway', 'goip-gateway', 'vicidial-dialer', 'magnusbilling']);

/** A PBX, SIP gateway or anything else that should answer on the SIP port. */
export function isSipDevice(resource: AccessResourceLike): boolean {
  if (SIP_DEVICE_KINDS.has(resource.kind)) return true;
  if (isAsterisk(resource) || isYeastar(resource) || isDinstar(resource)) return true;
  return resource.tags?.some((tag) => /^(sip|voip|pbx|goip)$/i.test(tag)) ?? false;
}

/** Port-scan preset that says the most about this device in one run. */
export function accessPortPreset(resource: AccessResourceLike): string {
  if (isMikrotik(resource)) return 'mikrotik';
  if (isSipDevice(resource)) return 'voip';
  if (isNetworkDevice(resource)) return 'management';
  return 'common';
}

/* ------------------------------------------------------------------ */
/* Address                                                             */
/* ------------------------------------------------------------------ */

/**
 * The address to point diagnostics and access links at.
 *
 * `config.host` wins over `address`: the former is what the collector talks to,
 * and diagnosing something other than what we poll would be actively
 * misleading.
 */
export function accessHostFor(resource: AccessResourceLike): string | null {
  const raw = String(resource.config?.host ?? resource.address ?? '').trim();
  if (!raw) return null;

  // Address fields collect pasted URLs and host:port pairs; reduce either to a
  // bare host so it can be validated and put back together deliberately.
  let host = raw;
  if (host.includes('://')) {
    try {
      host = new URL(host).hostname.replace(/^\[|\]$/g, '');
    } catch {
      return null;
    }
  } else {
    host = stripPort(host.split('/')[0]!);
  }

  return isValidTarget(host) ? host : null;
}

/** Strip a trailing `:port` without mangling a bare IPv6 literal. */
function stripPort(value: string): string {
  if (value.startsWith('[')) {
    const close = value.indexOf(']');
    return close > 0 ? value.slice(1, close) : value;
  }
  // A single colon is host:port; several mean this is an IPv6 address.
  return (value.match(/:/g) ?? []).length === 1 ? value.split(':')[0]! : value;
}

/** Login name for the access URIs, if the resource carries one. */
export function accessUsername(config: Record<string, unknown> = {}): string | null {
  for (const key of ['username', 'sshUsername', 'sshUser', 'user', 'adminUser']) {
    const value = config[key];
    if (typeof value === 'string' && value.trim() && !value.startsWith('•')) return value.trim();
  }
  return null;
}

/**
 * Every distinct host this resource answers on — the primary address, then
 * each secondary address that doesn't resolve to a host already in the list.
 * Order matters: a caller that tries hosts in turn and stops at the first
 * one that works should prefer the primary, same as `accessEndpointsFor`.
 */
export function registeredHosts(resource: AccessResourceLike): string[] {
  const hosts: string[] = [];
  const primary = accessHostFor(resource);
  if (primary) hosts.push(primary);
  for (const entry of resource.secondaryAddresses ?? []) {
    const host = addressHostFrom(entry);
    if (host && !hosts.includes(host)) hosts.push(host);
  }
  return hosts;
}

/* ------------------------------------------------------------------ */
/* Endpoints                                                           */
/* ------------------------------------------------------------------ */

function toPort(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const port = Number(value);
  // 0 is a real answer — it means "this device does not offer that service".
  if (!Number.isInteger(port) || port < 0 || port > 65535) return null;
  return port;
}

function configuredPort(config: Record<string, unknown>, service: AccessService): number | null {
  const explicit = toPort(config[ACCESS_SERVICE_SPECS[service].portKey]);
  if (explicit !== null) return explicit;

  // `webPort` predates these fields and several plugins already collect it.
  // Honour it for whichever of http/https it actually denotes.
  const webPort = toPort(config.webPort);
  if (webPort !== null && webPort > 0) {
    if (service === 'https' && webPort === 443) return 443;
    if (service === 'http' && webPort !== 443) return webPort;
  }
  return null;
}

export function accessUri(service: AccessService, host: string, port: number, username?: string | null): string {
  const spec = ACCESS_SERVICE_SPECS[service];
  const auth = spec.usesUsername && username ? `${encodeURIComponent(username)}@` : '';
  // A bare IPv6 literal has to be bracketed before it can carry a port.
  const hostPart = host.includes(':') ? `[${host}]` : host;

  // Leave the port off default web URLs, so the link is the one the operator
  // would have typed themselves.
  const web = service === 'http' || service === 'https';
  const portPart = web && port === spec.defaultPort ? '' : `:${port}`;

  return `${spec.scheme}://${auth}${hostPart}${portPart}`;
}

/**
 * Every door into this resource, in a fixed order.
 *
 * The base set is what any addressable host plausibly answers on; network gear
 * adds telnet and MikroTik adds Winbox. An explicitly configured port opts a
 * service in even where the kind would not have offered it.
 */
/**
 * A secondary address may be a bare host/IP or a full URL (the field takes
 * either — see `packages/shared/src/resources.ts`); either way, reduce it to
 * the bare host `accessEndpointsFor` needs to build a port-specific URI from.
 */
export function addressHostFrom(entry: AddressEntry): string | null {
  const raw = entry.value.trim();
  if (!raw) return null;
  if (raw.includes('://')) {
    try {
      return new URL(raw).hostname.replace(/^\[|\]$/g, '');
    } catch {
      return null;
    }
  }
  const host = stripPort(raw.split('/')[0]!);
  return isValidTarget(host) ? host : null;
}

/**
 * Every door into this resource, in a fixed order — across every address it
 * has, not just the primary one. A device with a management IP and a WAN IP
 * is reachable on both, and an operator should not have to add the WAN one as
 * a second resource just to SSH into it.
 *
 * Per-service custom ports (`config.sshPort`, ...) apply to the primary
 * address only — there is nowhere to hang a per-secondary-address override,
 * so a secondary address always offers each service on its default port.
 */
export function accessEndpointsFor(resource: AccessResourceLike): AccessEndpoint[] {
  const primaryHost = accessHostFor(resource);
  const config = resource.config ?? {};
  const username = accessUsername(config);
  const mikrotik = isMikrotik(resource);

  const offered = new Set<AccessService>(['ssh', 'http', 'https']);
  if (mikrotik || isNetworkDevice(resource)) offered.add('telnet');
  if (mikrotik) offered.add('winbox');
  if (isAsterisk(resource)) offered.add('ami');
  if (isSipDevice(resource)) offered.add('sip');

  const sources: Array<{ addressLabel: string | null; host: string }> = [];
  if (primaryHost) sources.push({ addressLabel: null, host: primaryHost });
  for (const entry of resource.secondaryAddresses ?? []) {
    const host = addressHostFrom(entry);
    // A secondary address that resolves to the same host as the primary (or
    // an earlier secondary) is not a second door — skip it rather than
    // listing "SSH" twice with nothing to tell the rows apart.
    if (host && !sources.some((s) => s.host === host)) sources.push({ addressLabel: entry.label || null, host });
  }

  const endpoints: AccessEndpoint[] = [];
  for (const source of sources) {
    for (const service of ACCESS_SERVICES) {
      const configured = source.addressLabel === null ? configuredPort(config, service) : null;
      if (configured === 0) continue;
      if (configured === null && !offered.has(service)) continue;

      const spec = ACCESS_SERVICE_SPECS[service];
      const port = configured ?? spec.defaultPort;
      endpoints.push({
        service,
        label: spec.label,
        host: source.host,
        port,
        uri: accessUri(service, source.host, port, username),
        standardScheme: spec.standardScheme,
        help: spec.help,
        proxyable: service === 'http' || service === 'https',
        custom: configured !== null,
        addressLabel: source.addressLabel,
        open: null,
        connectMs: null,
      });
    }
  }
  return endpoints;
}

/* ------------------------------------------------------------------ */
/* Device config cache                                                 */
/* ------------------------------------------------------------------ */

/**
 * Cached `/export` from a MikroTik resource, refreshed daily so an operator
 * (or the AI analyst) can see actual firewall rules, NAT, routing and
 * interface config, not just health metrics. Secrets are redacted server-side
 * before storage, so `configText` is safe to display or hand to an LLM.
 */
export interface DeviceConfig {
  resourceId: string;
  vendor: string;
  configText: string | null;
  lineCount: number;
  fetchedAt: number | null;
  error: string | null;
  updatedAt: number;
}
